import { useDispatch, useSelector } from "react-redux";
// @ts-expect-error: ignoring as it is existing js file
import { setValidationRequireFieldError, userDetailsSelector } from "@/store/slices/applicationFormSlice";
import type { ApplicationFormSlice, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
import { ConditionalSection } from "@/providers/LenderCardContextProvider/types/LenderProductTypes";
import { invalidKey } from "./utils/conditionalFields";
import type { RequiredFieldsLabel } from "./useRequiredFieldsLabel";

interface CurrentRequiredFields {
  asset: RequiredFieldsLabel[];
  customer: RequiredFieldsLabel[];
  entity: RequiredFieldsLabel[];
  agreement: RequiredFieldsLabel[];
  loanDetails: RequiredFieldsLabel[];
}

const useShowRequiredFields = () => {
  const dispatch = useDispatch();
  const { validationRequireFieldError } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);

  const showRequiredFields = (currentRequiredFields: CurrentRequiredFields) => {
    const { asset, customer, entity, agreement, loanDetails } = currentRequiredFields;

    const assetsFieldsErrorMessage = asset.reduce(
      (acc: Record<string, string>, curr) => ((acc[curr.fieldName] = curr.requiredMessage), acc),
      {},
    );

    const customerFieldsErrorMessage = customer.reduce(
      (acc: Record<string, string>, curr) => ((acc[curr.fieldName] = curr.requiredMessage), acc),
      {},
    );

    const agreementFieldsErrorMessage = agreement.reduce(
      (acc: Record<string, string>, curr) => ((acc[curr.fieldName] = curr.requiredMessage), acc),
      {},
    );

    const entityFieldsErrorMessage = entity.reduce(
      (acc: Record<string, string>, curr) => ((acc[curr.fieldName] = curr.requiredMessage), acc),
      {},
    );

    const loanDetailsFieldsErrorMessage = loanDetails.reduce(
      (acc: Record<string, string>, curr) => ((acc[curr.fieldName] = curr.requiredMessage), acc),
      {},
    );

    dispatch(
      setValidationRequireFieldError({
        ...validationRequireFieldError,
        agreementError: {
          ...agreementFieldsErrorMessage,
        },
        assetErrors: {
          ...assetsFieldsErrorMessage,
        },
        entityFieldErrors: {
          ...entityFieldsErrorMessage,
        },
        customerErrors: {
          ...customerFieldsErrorMessage,
        },
        loanDetailsErrors: {
          ...loanDetailsFieldsErrorMessage,
        },
      }),
    );
  };

  const resetInvalidRequiredFieldsByFieldName = ({ section, fieldName }: { section: string; fieldName: string }) => {
    const { assetErrors, customerErrors, entityFieldErrors, loanDetailsErrors, agreementError } =
      validationRequireFieldError;

    switch (section) {
      case ConditionalSection.ASSET: {
        const filteredKeys = assetErrors ? Object.keys(assetErrors).filter((k) => k.includes(fieldName)) : undefined;
        const foundKey = filteredKeys?.find((k) => k.includes(invalidKey));
        if (foundKey) {
          const updatedAssetErrors = { ...assetErrors };
          // Delete the error with the invalid key.
          delete updatedAssetErrors[foundKey];
          dispatch(
            setValidationRequireFieldError({
              ...validationRequireFieldError,
              assetErrors: updatedAssetErrors,
            }),
          );
        }
        break;
      }
      case ConditionalSection.CUSTOMER: {
        const filteredKeys = customerErrors
          ? Object.keys(customerErrors).filter((k) => k.includes(fieldName))
          : undefined;
        const foundKey = filteredKeys?.find((k) => k.includes(invalidKey));
        if (foundKey) {
          const updatedCustomerErrors = { ...customerErrors };
          // Delete the error with the invalid key.
          delete updatedCustomerErrors[foundKey];
          dispatch(
            setValidationRequireFieldError({
              ...validationRequireFieldError,
              customerErrors: updatedCustomerErrors,
            }),
          );
        }
        break;
      }
      case ConditionalSection.ENTITY: {
        const filteredKeys = entityFieldErrors
          ? Object.keys(entityFieldErrors).filter((k) => k.includes(fieldName))
          : undefined;
        const foundKey = filteredKeys?.find((k) => k.includes(invalidKey));
        if (foundKey) {
          const updatedEntityFieldErrors = { ...entityFieldErrors };
          // Delete the error with the invalid key.
          delete updatedEntityFieldErrors[foundKey];
          dispatch(
            setValidationRequireFieldError({
              ...validationRequireFieldError,
              entityFieldErrors: updatedEntityFieldErrors,
            }),
          );
        }
        break;
      }
      case ConditionalSection.LOAN_DETAILS: {
        const filteredKeys = loanDetailsErrors
          ? Object.keys(loanDetailsErrors).filter((k) => k.includes(fieldName))
          : undefined;
        const foundKey = filteredKeys?.find((k) => k.includes(invalidKey));
        if (foundKey) {
          const updatedLoanDetailsErrors = { ...loanDetailsErrors };
          // Delete the error with the invalid key.
          delete updatedLoanDetailsErrors[foundKey];
          dispatch(
            setValidationRequireFieldError({
              ...validationRequireFieldError,
              loanDetailsErrors: updatedLoanDetailsErrors,
            }),
          );
        }
        break;
      }
      case ConditionalSection.AGREEMENT: {
        const filteredKeys = agreementError
          ? Object.keys(agreementError).filter((k) => k.includes(fieldName))
          : undefined;
        const foundKey = filteredKeys?.find((k) => k.includes(invalidKey));
        if (foundKey) {
          const updatedAgreementError = { ...agreementError };
          // Delete the error with the invalid key.
          delete updatedAgreementError[foundKey];
          dispatch(
            setValidationRequireFieldError({
              ...validationRequireFieldError,
              agreementError: updatedAgreementError,
            }),
          );
        }
        break;
      }
    }
  };

  return {
    showRequiredFields,
    resetInvalidRequiredFieldsByFieldName,
  };
};

export { useShowRequiredFields };
